import { Alert, Typography, Autocomplete, Grid, List, ListItem, TextField } from '@mui/material'
import { Box } from '@mui/system'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import React, { useEffect, useState } from 'react'
import DefaultModalActions from '../GenericModal/DefaultModalActions'
import { type CsvHeader } from '../../models/CsvHeader'
import pluralize from 'pluralize'
import { grey } from '../../colors'

interface CsvFieldMappingProps {
  title: string
  userHeaders: string[]
  backendHeaders: CsvHeader[]
  previewData: string[][]
  onSuccess: (a: Record<string, string>) => void
  onClose: () => void
  loading?: boolean
}

const CsvFieldMapping: React.FC<CsvFieldMappingProps> = ({
  title,
  userHeaders,
  backendHeaders,
  previewData,
  onSuccess,
  onClose,
  loading = false
}): JSX.Element => {
  const [error, setError] = useState<string[]>()

  const [userToBackendMapping, setUserToBackendMapping] = useState<Record<string, CsvHeader | null>>({})

  useEffect(() => {
    const calculatedMapping: Record<string, CsvHeader | null> = {}

    for (const userHeader of userHeaders) {
      calculatedMapping[userHeader] = null
    }

    for (const csvHeader of backendHeaders) {
      if (userHeaders.includes(csvHeader.headerName)) {
        calculatedMapping[csvHeader.headerName] = csvHeader
      }
    }

    setUserToBackendMapping(calculatedMapping)
  }, [userHeaders])

  function showLabel (header: CsvHeader): string {
    let name = header.headerName
    if (header.required) {
      name += '*'
    }
    if (header.multiple) {
      name += '+'
    }
    return name
  }

  const selectableBackendHeaders: CsvHeader[] = []

  const usedBackendHeaders = Object.values(userToBackendMapping ?? {}).filter(h => h !== null) as CsvHeader[]

  for (const backendHeader of backendHeaders) {
    // headers that are multiple are always selectable
    if (backendHeader.multiple) {
      selectableBackendHeaders.push(backendHeader)
      continue
    }

    const found = usedBackendHeaders.find((h: CsvHeader) => h.headerName === backendHeader.headerName)
    if (!found) {
      selectableBackendHeaders.push(backendHeader)
    }
  }

  // Sort and group headers by required and optional
  selectableBackendHeaders.sort((a, b) => {
    if (a.required !== b.required) {
      return a.required ? -1 : 1
    }
    if (a.multiple !== b.multiple) {
      return a.multiple ? 1 : -1
    }
    return 0
  })

  const unusedRequiredFields: string[] = selectableBackendHeaders.filter(h => h.required).map(h => h.headerName)

  const uploadFunc = (): void => {
    if (unusedRequiredFields?.length > 0) {
      setError(unusedRequiredFields)
      return
    }

    const resultUserMapping: Record<string, string> = {}
    for (const header of userHeaders) {
      // remove null entries only
      if (userToBackendMapping[header] !== null) {
        resultUserMapping[header] = (userToBackendMapping[header]?.headerName as string)
      }
    }
    onSuccess(resultUserMapping)
    setError(undefined)
  }

  return (
    <>
      <Box>
        {error &&
          <Alert severity="error">
            Error: The following fields are required
            <List dense disablePadding sx={{ listStyleType: 'disc', pl: 4 }}>
              {error.map(e => (
                <ListItem key={e} sx={{ display: 'list-item', p: 0 }}>
                  {e}
                </ListItem>
              ))}
            </List>
          </Alert>
        }
        <Grid container gap={4}>
          <Grid container xs={12} alignItems="center">
            <Typography variant="p3">
              Map the columns in your CSV to {pluralize.singular(title)} in Classranked.
              <br />
              Fields with * are required
              <br />
              Fields with + can be selected multiple times
            </Typography>

          </Grid>
          <Grid container xs={12} alignItems="center">
            <Grid xs={5.5} item>
              <Typography variant="p3" sx={{ fontWeight: 'bold' }}>
                Columns in your File
              </Typography>
            </Grid>
            <Grid xs={1} item />
            <Grid xs={5.5} item>
              <Typography variant="p3" sx={{ fontWeight: 'bold' }}>
                {pluralize.singular(title)} fields
              </Typography>
            </Grid>
          </Grid>

          {Object.keys(userToBackendMapping).map(userHeader => {
            const error = unusedRequiredFields?.length > 0 && userToBackendMapping[userHeader] === null
            // Concatenate the preview data for the current column
            const previewText = previewData.map(row => {
              const cell = row[userHeaders.indexOf(userHeader)]
              return cell || '[No data]'
            }).join(', ')
            return (
              <Grid container xs={12} key={userHeader}>
                <Grid xs={5.5} item>
                  <TextField
                    value={userHeader}
                    helperText={previewText}
                    disabled
                    sx={{
                      width: '100%',
                      '& .MuiFormHelperText-root': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '100%'
                      }
                    }}
                  />
                </Grid>
                <Grid xs={1} textAlign="center" sx={{ pt: 2.5 }}><ArrowForwardIcon /></Grid>
                <Grid xs={5.5} item>
                  <Autocomplete
                    size="small"
                    filterOptions={(options) => options}
                    options={selectableBackendHeaders}
                    groupBy={(option) => option.required ? 'Required' : 'Optional'}
                    getOptionLabel={showLabel}
                    clearOnBlur={true}
                    value={userToBackendMapping[userHeader] ?? undefined}
                    onChange={(e, v: CsvHeader | null, reason) => {
                      if (reason === 'clear' || !v) {
                        setUserToBackendMapping({
                          ...userToBackendMapping,
                          [userHeader]: null
                        })
                        return
                      }
                      setUserToBackendMapping({
                        ...userToBackendMapping,
                        [userHeader]: v
                      })
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Skip or Select Field" helperText={error ? 'Map all required fields to upload.' : undefined} error={error} />}
                    renderGroup={(params) => {
                      return (
                        <React.Fragment key={params.key}>
                          <li style={{ paddingTop: '4px' }}>
                            <Typography variant="subtitle2" color="textPrimary" style={{ padding: '4px 0 4px 8px', textTransform: 'uppercase', backgroundColor: grey[100] }}>{params.group}</Typography>
                            <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>{params.children}</ul>
                          </li>
                        </React.Fragment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            )
          })
          }
        </Grid>
      </Box>
      <DefaultModalActions
        confirmButtonProps={{
          text: 'Upload',
          onClick: uploadFunc,
          loading
        }}
        destructiveButtonProps={{
          onClick: onClose,
          text: 'Cancel'
        }}
      />
    </>
  )
}

export default CsvFieldMapping
